import React, { useCallback, useEffect, useRef } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { useNavigate } from 'react-router-dom';
import { Box } from '@material-ui/core';
import TokenService from 'services/auth/TokenService';
import { selectIsMemberPortalUserLoggedIn } from 'memberPortalModules/authentication/state/selectors';
import { memberPortalLogOut } from 'memberPortalModules/authentication/state/actions';
import { useAppDispatch } from 'store/hooks';
import useZendeskWidget from 'common/hooks/useZendeskWidget';
import { Routes } from 'constants/route.constants';
import { fetchInvoiceShortDataThunk } from 'memberPortalModules/MemberProfile/state/invoice/actions';
import {
  selectMemberProfile,
  selectMemberProfileLoading,
} from 'memberPortalModules/MemberProfile/state/profile/selectors';
import { fetchProfileInfoView } from 'memberPortalModules/MemberProfile/state/profile/actions';
import {
  selectInvoiceShortData,
  selectInvoiceShortDataLoading,
} from 'memberPortalModules/MemberProfile/state/invoice/selectors';
import { IInvoiceShortData } from 'memberPortalModules/MemberProfile/interface';

interface ICustomRouteProps {
  secure?: boolean;
  publicAccess?: boolean;
  children: React.ReactElement;
}

const CustomRoute = ({ secure, publicAccess, children }: ICustomRouteProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const refIsFetchedOnce = useRef<boolean>(false);
  const navigate = useNavigate();

  const isUserLoggedIn: boolean = useRootSelector(selectIsMemberPortalUserLoggedIn);
  const isExistProfile = useRootSelector(selectMemberProfile);
  const isProfileLoading: boolean = useRootSelector(selectMemberProfileLoading);
  const invoiceUnitAmount: IInvoiceShortData = useRootSelector(selectInvoiceShortData);
  const isInvoiceUnitAmountLoading: boolean = useRootSelector(selectInvoiceShortDataLoading);

  const routePreActions = useCallback(() => {
    const refreshToken = TokenService.getRefreshToken();
    const isRefreshExpired = TokenService.isTokenExpired(refreshToken);

    if (isRefreshExpired && isUserLoggedIn) {
      dispatch(memberPortalLogOut());
    }

    if (secure && !publicAccess && !isUserLoggedIn) {
      // TODO: Need to add reset role
      navigate(Routes.SIGN_IN);
    }

    if (secure && isUserLoggedIn && !isRefreshExpired) {
      if (!isExistProfile && !isProfileLoading) {
        dispatch(fetchProfileInfoView());
      }

      if (!refIsFetchedOnce.current && invoiceUnitAmount === null && !isInvoiceUnitAmountLoading) {
        refIsFetchedOnce.current = true;
        dispatch(fetchInvoiceShortDataThunk());
      }
    }
  }, [
    isUserLoggedIn,
    secure,
    publicAccess,
    dispatch,
    navigate,
    isExistProfile,
    isProfileLoading,
    invoiceUnitAmount,
    isInvoiceUnitAmountLoading,
  ]);

  useEffect(() => {
    routePreActions();
  }, [routePreActions]);

  useEffect(() => {
    if (secure && !isUserLoggedIn && !publicAccess) {
      // TODO: Need to add reset role
      navigate(Routes.SIGN_IN);
    }
  }, [isUserLoggedIn, navigate, secure, publicAccess]);

  useZendeskWidget();

  return <Box height="100%">{isUserLoggedIn && children}</Box>;
};

export default CustomRoute;
